import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BaseInstructionCard, BaseInstructionCardPassDownProps } from '../BaseInstructionCard'
import { FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { formatPrice, valueMultiplied } from 'utils/price'
import { usePurchaseFlowConfig, usePurchaseFlowOrderMeta, usePurchaseFlowProducts, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'

import Big from 'big.js'
import { Color } from 'constants/assets'
import { CreativesTable, type CreativesTableRow } from 'components/common/CreativesTable'
import { Currency } from 'constants/misc'
import { GetAvailableCreativesResponse, PurchaseSessionWrapper } from 'dataQueries/purchase.query'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { ProductKind } from 'constants/product'
import ReactLoading from 'react-loading'
import { SearchOutlined } from '@mui/icons-material'
import { Stack } from '@mui/material'
import { SubscriptionInstructionUpsell } from '../../../SubscriptionInstructionUpsell'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { FavouriteCreative, TravelCostProduct } from 'models/purchaseFlow'
import styles from './BookCreativeCard.module.sass'
import { useAuth0 } from 'utils/auth'
import { useBookCreative } from '../../_main/contexts'
import { useDebouncedEffect } from 'utils/helpers'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Card for Choose your own creative organization instruction.
 * Fetches available creatives on load and displays either
 * - loading
 * - table with available creatives for selection
 * - selected CT
 * - badge with warning, that no CTs are available
 * 
 * For non-subscribed users the card is disabled with up-sell info and link.   
 * Admins are treated as unsubscribed by default
 * 
 * DEPENDENCIES:
 * - PurchaseFlowOverviewContextProvider
 * - TargetOrderUserProvider
 * - ProductAndInstructionsListProvider
 * - InstructionStepProvider
 * - BookCreativeContextProvider
 * 
 * @example
 * <BookCreativeCard
 *  isCardDisabled={false}
 *  isSelectionDisabled={false}
 *  instructionOption={instructionOptionDTO}
 *  instructionType={InstructionType.BILLING_PRIMARY}
 *  onChange={(changed) => console.log}
 * />
 */
export const BookCreativeCard: FC<BaseInstructionCardPassDownProps> = ({
  isCardDisabled,
  isSelectionDisabled,
  instructionOption,
  instructionType,
}) => {

  const { t } = useTranslation(['book_your_ct_card'])
  const { roles } = useAuth0()
  const { clientData, userPropertyWorkspaceId } = useUserData()
  const { isEmbed } = useEmbedConfig()
  const { targetUser } = useTargetOrderUser()
  const { sessionId, selectedAssignmentPlace, selectedCountryCode } = usePurchaseFlowConfig()
  const { selectedExtraServices, toggleExtraService, setSelectedExtraServices } = usePurchaseFlowOrderMeta()
  const { selectedProducts, selectedProductOptions, selectedProductOptionsKey } = usePurchaseFlowProducts()
  const {
    availableCreatives,
    workspaceCreatives,
    getAvailableCTs,
    selectedCreative,
    setSelectedCreative,
  } = useBookCreative()

  const travelCostOf = (creative: FavouriteCreative) =>
    valueMultiplied(creative.travelCost.productUnitPrice, creative.travelCost.productQuantity)

  const toTableRow = (creative: FavouriteCreative): CreativesTableRow => ({
    id: String(creative.id),
    fullName: creative.fullName,
    phoneNumber: creative.phoneNumber || undefined,
    travelCost: { amount: travelCostOf(creative).toNumber(), currency: creative.travelCost.productUnitPrice.currency },
  })

  const chooseCreative = (id: string) => {
    const creative = [...availableCreatives, ...workspaceCreatives].find((candidate) => String(candidate.id) === id)
    if (!creative) return
    logAnalyticsEvent(AnalyticsEvent.CREATIVE_SELECTED_BY_CLIENT, {
      transportCosts: formatPrice(travelCostOf(creative), creative.travelCost.productUnitPrice.currency),
      creativeFullName: creative.fullName,
    })
    setSelectedCreative(creative)
  }

  const isSelected = useMemo(() => selectedExtraServices.has(ProductKind.FAVOURITE_CREATIVE), [selectedExtraServices])

  // In the embed the subscription journey dead-ends (the broker is a shop MEMBER, subscribing needs ADMIN
  // and a non-Propstack workspace), so the locked badge stays but the CTA is dropped (ISSUE-3097)
  const showSubscriptionUpsell = !isEmbed && !targetUser.isSubscribed

  const [nameFilter, setNameFilter] = useState<string>('')
  const [isNoCTAvailable, setIsNoCTAvailable] = useState<boolean>()

  const offeredTransportCosts: { highestCost: number, lowestCost?: number, currency?: Currency } = useMemo(() => {
    let currency: Currency | undefined = undefined
    let highestCost: Big = Big(0)
    let lowestCost: Big | undefined = undefined

    const creativeList = [...availableCreatives, ...workspaceCreatives]

    if (creativeList.length > 0) {
      for (const creative of creativeList) {
        const callbackCost = (data: TravelCostProduct) => valueMultiplied(data.productUnitPrice, data.productQuantity)
        let cost = callbackCost(creative.travelCost)
        if (!lowestCost) lowestCost = cost
        if (cost > highestCost) highestCost = cost
        if (cost < lowestCost) lowestCost = cost
      }
      currency = creativeList[0].travelCost.productUnitPrice.currency
    }
    // Avoid empty value for lowest
    else lowestCost = highestCost

    return { highestCost: highestCost.toNumber(), lowestCost: lowestCost?.toNumber(), currency }
  }, [availableCreatives, workspaceCreatives])

  // An empty list is what the country filter makes likely, and it silently unselects the paid
  // service below — so both fetches report it rather than only the first one
  const onCreativesSettled = useCallback((trigger: 'initial' | 'refine') =>
    (data: PurchaseSessionWrapper<GetAvailableCreativesResponse> | undefined, error: unknown) => {
      const creatives = data?.data.data
      const reason = (!!error || !creatives)
        ? 'request_failed'
        : (!creatives.availableCreatives.length && !creatives.workspaceCreatives.length ? 'empty_list' : null)

      setIsNoCTAvailable(reason !== null)

      if (reason) logAnalyticsEvent(AnalyticsEvent.FAVOURITE_CREATIVES_UNAVAILABLE, { reason, trigger })
    }, [setIsNoCTAvailable])

  // Initial get
  const isInited = useRef(false)

  useEffect(() => {
    if (isInited.current) return
    if (!isSelected) return
    if (isCardDisabled) return
    if (selectedCreative) return
    if (!targetUser.isSubscribed) return
    if (!targetUser.id) return
    if (!sessionId || !selectedAssignmentPlace) return

    const selectedProductIds = [
      ...Object.values(selectedProducts).map(product => product.id),
      ...Object.values(selectedProductOptions).flatMap(options => Object.values(options).map(option => option.id)),
    ]

    // Set before the call, not in onSettled: a dependency changing mid-flight would otherwise
    // fire a second identical request whose settle races the first
    isInited.current = true

    getAvailableCTs.mutate(
      {
        workspaceId: userPropertyWorkspaceId,
        role: roles,
        sessionId,
        selectedProductIds,
        address: selectedAssignmentPlace.formatted_address,
        coordinates: selectedAssignmentPlace.geometry.location,
        countryCode: selectedCountryCode,
        clientUserId: targetUser.id,
        name: undefined,
      },
      { onSettled: onCreativesSettled('initial') }
    )

    // getAvailableCTs dependency is ignored to prevent looping
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAvailableCTs.mutate, isCardDisabled, isSelected, onCreativesSettled, selectedAssignmentPlace, selectedCountryCode, selectedCreative, selectedProducts, selectedProductOptionsKey, sessionId, targetUser.isSubscribed, targetUser.id, setIsNoCTAvailable])

  // Call based on name filter change
  useDebouncedEffect(
    () => {
      if (!isInited.current) return
      if (!isSelected) return
      if (isCardDisabled) return
      if (selectedCreative) return
      if (!targetUser.isSubscribed) return
      if (!targetUser.id) return
      if (!sessionId || !selectedAssignmentPlace) return

      const selectedProductIds = [
        ...Object.values(selectedProducts).map(product => product.id),
        ...Object.values(selectedProductOptions).flatMap(options => Object.values(options).map(option => option.id)),
      ]

      getAvailableCTs.mutate(
        {
          workspaceId: userPropertyWorkspaceId,
          role: roles,
          sessionId,
          selectedProductIds,
          address: selectedAssignmentPlace.formatted_address,
          coordinates: selectedAssignmentPlace.geometry.location,
          countryCode: selectedCountryCode,
          clientUserId: targetUser.id,
          name: nameFilter || undefined,
        },
        { onSettled: onCreativesSettled('refine') }
      )
    },
    [getAvailableCTs.mutate, isCardDisabled, isSelected, onCreativesSettled, selectedAssignmentPlace, selectedCountryCode, selectedCreative, selectedProducts, selectedProductOptionsKey, sessionId, targetUser.isSubscribed, targetUser.id, nameFilter],
    350
  )

  useEffect(() => {
    if (isSelected) {
      logAnalyticsEvent(AnalyticsEvent.SELECT_CREATIVE_OPTION_OPENED, {
        lowestTransportCost: offeredTransportCosts.lowestCost,
        highestTransportCost: offeredTransportCosts.highestCost,
        currency: offeredTransportCosts.currency,
      })
    }

    /* 
      Omit everything except isSelected to prevent re-trigger of logging when the values change
      as we are only interested in values current at the time of change os isSelected
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected])

  /** Ensure the card is unselected if noCT is available or user is not subscribed */
  useEffect(() => {
    if (isSelected && (isNoCTAvailable || !targetUser.isSubscribed)) {
      setSelectedExtraServices((currentServices) => {
        const newServices = new Set(currentServices)
        newServices.delete(instructionOption.kind)
        return newServices
      })
    }
  }, [isNoCTAvailable, targetUser.isSubscribed, instructionOption.kind, selectedExtraServices, setSelectedExtraServices, isSelected])

  const callCreativeNote: ReactNode = (
    <div className={styles.note}>

      <h3>{t('call_ct_title')}</h3>

      {/* Handles strong/br tags */}
      <Trans t={t} i18nKey="call_ct_text" parent="p">
        &nbsp;
      </Trans>

    </div>
  )

  return (
    <BaseInstructionCard
      instructionType={instructionType}
      isSelectionDisabled={isSelectionDisabled || isNoCTAvailable || !targetUser.isSubscribed}
      instructionOption={instructionOption}
      isCardDisabled={isCardDisabled || isNoCTAvailable || !targetUser.isSubscribed}
      isChecked={isSelected}
      additionalBottomBoxContent={showSubscriptionUpsell && (
        <SubscriptionInstructionUpsell
          title={t('upsell_title')}
          text={t('upsell_text')}
          onClick={() =>
            logAnalyticsEvent('unblock_SaaS_select_creative', {
              userEmail: clientData?.email,
              organizationId: clientData?.organizationId,
            })
          }
        />
      )}
      onCheck={() => toggleExtraService(instructionOption.kind)}
      badges={[
        (!targetUser.isSubscribed
          ? { color: 'coral', text: t('sub_only_badge'), materialIcon: <LockOutlinedIcon /> }
          : undefined
        )
      ]}
      rightHeaderContent={(isNoCTAvailable || getAvailableCTs.isPending) && (
        <Fragment>
          {getAvailableCTs.isPending && <ReactLoading className={styles.loading} color={Color.GRAY_TEXT} type="spin" />}
          {isNoCTAvailable && <MUIBadge color="coral" label={t('no_ct_badge')} />}
        </Fragment>
      )
      }
    >
      <div className={styles.bookCreativeCard}>

        {/* LOADING */}
        {getAvailableCTs.isPending && isNoCTAvailable === undefined && (
          <TransitionAppear visible>
            <ReactLoading className={styles.innerLoading} color={Color.GRAY_TEXT} type="spin" />
          </TransitionAppear>
        )}

        {/* LIST */}
        {(getAvailableCTs.isSuccess || isNoCTAvailable !== undefined) && !selectedCreative &&
          <Stack gap="3rem">

            {callCreativeNote}

            <MUIInputField
              label={t('search_label')}
              value={nameFilter}
              startIcon={getAvailableCTs.isPending
                ? <ReactLoading className={styles.loading} color={Color.GRAY_TEXT} type="spin" />
                : <SearchOutlined fontSize="large" />
              }
              onChange={(e) => {
                setNameFilter(e.target.value)
              }}
            />

            <CreativesTable creatives={availableCreatives.map(toTableRow)} title={t('previously_booked_cts')} onChoose={chooseCreative} />

            <CreativesTable creatives={workspaceCreatives.map(toTableRow)} title={t('workspace_favourite_cts')} onChoose={chooseCreative} />

          </Stack>
        }

        {/* SELECTED */}
        {getAvailableCTs.isSuccess && !!selectedCreative &&
          <Stack gap="3rem">

            {callCreativeNote}

            <div>
              <h2>{t('chosen_ct')}</h2>

              <div className={styles.selectedCreative}>
                <span>{selectedCreative.fullName}</span>
                <span>{selectedCreative.phoneNumber || '-'}</span>
                <span>{
                  formatPrice(
                    valueMultiplied(
                      selectedCreative.travelCost.productUnitPrice,
                      selectedCreative.travelCost.productQuantity
                    ),
                    selectedCreative.travelCost.productUnitPrice.currency
                  )}
                </span>
                {!isSelectionDisabled && (
                  <MUIButton
                    type="secondaryBorder"
                    size="sm"
                    className={styles.action}
                    onClick={() => setSelectedCreative(null)}
                  >
                    {t('change')}
                  </MUIButton>
                )}
              </div>
            </div>

          </Stack>
        }

      </div>
    </BaseInstructionCard>
  )

}
